import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { initSentry, setSentryUser } from './config/sentry';
import './styles/sentry-overrides.css';
import { getDataSource } from './services/dataSource';
import { Meeting } from './shared/types';
import { dataLoadingService, LoadingCallbacks } from './services/DataLoadingService';
import { backgroundLoadingService } from './services/BackgroundLoadingService';
import { TimezoneProvider } from './context/TimezoneContext';
import { LanguageProvider } from './context/LanguageContext';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { CalendarPage } from './features/calendar/pages/CalendarPage';
import { MeetingsPage } from './features/meetings/pages/MeetingsPage';
import { PrivateRoute } from './components/PrivateRoute';
import { LoginPage } from './pages/LoginPage';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import { GlobalLoadingProvider, useGlobalLoading } from './context/GlobalLoadingContext';
import UnifiedLoadingScreen from './components/loading/UnifiedLoadingScreen';
import { ToastProvider } from './components/ToastProvider';
import { useTranslation } from 'react-i18next';
import './i18n';

// Initialize Sentry
initSentry();



// New AppContent component to hold the logic that uses useAuth
const AppContent = () => {
  // DEBUG: console.log(`AppContent component rendered/re-rendered - ${new Date().toISOString()}`);
  const { account, loading: authLoading, user } = useAuth();

  // Set user context in Sentry when authenticated
  useEffect(() => {
    if (user) {
      setSentryUser({
        id: user.uid,
        email: user.email || '',
        username: user.displayName || user.email || '',
      });
    }
  }, [user]);
  const { state: globalLoadingState, setCurrentStage, completeStage, setCurrentRoute, shouldLoadStage } = useGlobalLoading();
  const [meetings, setMeetingsRaw] = useState<Meeting[]>([]);

  // Wrapped setMeetings for potential debugging
  const setMeetings = (newMeetings: Meeting[]) => {
    // DEBUG: console.log(`setMeetings called: ${newMeetings.length} meetings - ${new Date().toISOString()}`);
    setMeetingsRaw(newMeetings);
  };
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMoreMeetings, setLoadingMoreMeetings] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasCompletedMeetingsStage = useRef(false);
  const hasSetupPeriodicRefresh = useRef(false);
  const location = useLocation();
  const pathname = location.pathname;
  const previousPathname = useRef<string | null>(null);
  const hasLoggedSkipForRoute = useRef<string | null>(null);

  // Debug component lifecycle
  useEffect(() => {
    // DEBUG: console.log(`AppContent component MOUNTED - ${new Date().toISOString()}`);
    return () => {
      // DEBUG: console.log(`AppContent component UNMOUNTED - ${new Date().toISOString()}`);
    };
  }, []);

  // Track route changes - only when route actually changes
  useEffect(() => {
    // Only update if route actually changed from a previous value
    if (previousPathname.current !== null && previousPathname.current !== pathname) {
      // console.log(`Route actually changed: ${previousPathname.current} → ${pathname} - ${new Date().toISOString()}`);
      setCurrentRoute(pathname);
    } else if (previousPathname.current === null && globalLoadingState.currentRoute !== pathname) {
      // Initial route setup
      setCurrentRoute(pathname);
    }
    previousPathname.current = pathname;
  }, [pathname, setCurrentRoute, globalLoadingState.currentRoute]);

  // Check if authentication stage is complete
  const isAuthenticationComplete = useMemo(() =>
    globalLoadingState.completedStages.includes('authentication'),
    [globalLoadingState.completedStages]
  );

  // Create callbacks for DataLoadingService
  const loadingCallbacks: LoadingCallbacks = useMemo(() => ({
    onLoadingStateChange: (state) => {
      // DEBUG: Track what's calling state changes
      // if (state.loadingInitial !== undefined || state.meetings !== undefined) {
      //   console.log(`onLoadingStateChange called:`, {
      //     loadingInitial: state.loadingInitial,
      //     meetingsLength: state.meetings?.length,
      //     timestamp: new Date().toISOString(),
      //     stack: new Error().stack?.split('\n')[2]?.trim()
      //   });
      // }

      if (state.loadingInitial !== undefined) setLoadingInitial(state.loadingInitial);
      if (state.loadingMoreMeetings !== undefined) setLoadingMoreMeetings(state.loadingMoreMeetings);
      if (state.error !== undefined) setError(state.error);
    },
    onStageStart: (stageId) => {
      setCurrentStage(stageId);
    },
    onStageComplete: (stageId) => {
      // Complete any stage that DataLoadingService reports as complete
      // Verbose debug log - commented for production (keep for major events)
      console.log(`Stage completed: ${stageId}`);
      completeStage(stageId);

      // Special handling for meetings stage to prevent duplicate completions
      if (stageId === 'meetings') {
        hasCompletedMeetingsStage.current = true;
      }
    },
    onError: (errorMessage) => {
      setError(errorMessage);
    },
    onMeetingsUpdate: (newMeetings) => {
      // DEBUG: console.log(`onMeetingsUpdate called: ${newMeetings.length} meetings received - ${new Date().toISOString()}`);
      setMeetings(newMeetings);
    }
  }), [setCurrentStage, completeStage]);

  // Initialize DataLoadingService
  useEffect(() => {
    dataLoadingService.initialize(loadingCallbacks);
  }, [loadingCallbacks]);

  // Initialize BackgroundLoadingService
  useEffect(() => {
    backgroundLoadingService.initialize({
      onStageStart: (stageId) => {
        // Background loading started for ${stageId}
        setCurrentStage(stageId);
      },
      onStageComplete: (stageId) => {
        // console.log(`Background loading completed for ${stageId}`);
        completeStage(stageId);
      },
      onError: (stageId, error) => {
        console.error(`Background loading failed for ${stageId}:`, error);
      }
    });
  }, [setCurrentStage, completeStage]);

  // Data loading effect - uses DataLoadingService
  useEffect(() => {
    const loadData = async () => {
      // Skip loading if auth is still in progress or no account/user
      if (authLoading || !account || typeof account !== 'string' || !user) {
        if (!authLoading && !account) {
          // Reset state when no account
          setLoadingInitial(false);
          setMeetings([]);
          setError(null);
          dataLoadingService.resetState();
        }
        return;
      }

      // Only proceed if authentication is complete
      if (!isAuthenticationComplete) {
        return;
      }

      // Check if already loading for this account
      if (dataLoadingService.isLoadingForAccount(account)) {
        console.log(`Data already loading for ${account}, skipping duplicate - ${new Date().toISOString()}`);
        return;
      }

      // Check if meetings should be loaded for current route
      // Use global loading context to determine if meetings are needed
      const shouldLoadMeetings = shouldLoadStage('meetings');
      if (!shouldLoadMeetings) {
        // Only log once per route to prevent spam
        if (hasLoggedSkipForRoute.current !== pathname) {
          // console.log(`Skipping meetings loading for route ${pathname} - not critical`);
          hasLoggedSkipForRoute.current = pathname;
        }
        return;
      }

      // Additional debug for meetings loading issue
      // Verbose debug log - commented for production
      // console.log(`Proceeding with meetings loading for route ${pathname} at ${new Date().toISOString()}`);

      // Start data loading with service
      try {
        await dataLoadingService.loadData({
          account,
          userId: user.uid,
          triggerSource: globalLoadingState.loadingSource || 'refresh'
        });
      } catch (error) {
        console.error('Data loading failed:', error);
        // Error handling is done by the service via callbacks
      }
    };

    // Reset meetings completion flag when account switching starts
    if (globalLoadingState.loadingSource === 'account-switch' && hasCompletedMeetingsStage.current) {
      console.log('Account switch detected - resetting meetings completion flag');
      hasCompletedMeetingsStage.current = false;
      hasSetupPeriodicRefresh.current = false;
    }

    // Skip if meetings are already loaded and stage is completed
    const meetingsCompleted = globalLoadingState.completedStages.includes('meetings');
    const hasMeetingsData = meetings.length > 0;

    if (user && isAuthenticationComplete && !meetingsCompleted) {
      loadData();
    } else if (user && isAuthenticationComplete && meetingsCompleted && hasMeetingsData) {
      // Meetings already loaded and completed - don't do anything
      console.log(`Meetings already loaded and completed (${meetings.length} meetings) - skipping effect`);
      return;
    } else if (!authLoading && !account) {
      // Reset when logged out
      setMeetings([]);
      setLoadingInitial(false);
      setLoadingMoreMeetings(false);
      setError(null);
      hasCompletedMeetingsStage.current = false;
      hasSetupPeriodicRefresh.current = false;
      dataLoadingService.resetState();
    }
  }, [account, authLoading, user, isAuthenticationComplete, globalLoadingState.loadingSource, pathname]);

  // Monitor background queue and trigger loading
  // ISOLATION FIX: Only depend on backgroundQueue changes, not completedStages
  // This prevents periodic refresh stage completions from triggering this effect
  useEffect(() => {
    if (!user || !account) return;

    // Check if meetings are in background queue and should be loaded
    // Use real-time check instead of dependency to avoid periodic interference
    if (globalLoadingState.backgroundQueue.includes('meetings') && !globalLoadingState.completedStages.includes('meetings')) {
      backgroundLoadingService.addToQueue('meetings', 10, async () => {
        if (!dataLoadingService.isLoadingForAccount(account)) {
          await dataLoadingService.loadData({
            account,
            userId: user.uid,
            triggerSource: 'refresh'
          });
        }
      });
    }
  }, [globalLoadingState.backgroundQueue, user, account]); // REMOVED: globalLoadingState.completedStages

  // Set up periodic meetings refresh with network awareness
  // ISOLATION FIX: Remove completedStages dependency to prevent periodic interference
  // Use real-time check instead of dependency for meetings completion status
  useEffect(() => {
    if (!user || !account) return;

    // Only set up periodic refresh if meetings have been loaded at least once
    // Use real-time check instead of dependency to avoid periodic refresh cycles triggering this
    if (globalLoadingState.completedStages.includes('meetings') && !hasSetupPeriodicRefresh.current) {
      console.log('Setting up periodic meetings refresh (every 5 minutes)');

      // Add periodic task for meetings refresh
      const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
      console.log(`Periodic refresh interval: ${REFRESH_INTERVAL / 1000} seconds`);

      backgroundLoadingService.addPeriodicTask(
        'meetings-periodic-refresh',
        REFRESH_INTERVAL, // 5 minutes
        5, // Lower priority than user-initiated actions
        async () => {
          console.log('Periodic meetings refresh triggered');

          // Network awareness: Skip refresh on slow connections
          const connection = (navigator as { connection?: { effectiveType?: string } }).connection;
          if (connection && (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g')) {
            console.log('Skipping periodic refresh on slow connection:', connection.effectiveType);
            return;
          }

          // Check if meetings cache is stale and not already loading
          const currentState = dataLoadingService.getLoadingState();
          if (currentState.currentAccount === account && !currentState.isLoading) {
            try {
              await dataLoadingService.refreshMeetings(account, user.uid, {
                skipCache: true,
                silent: true
              });
              console.log('Periodic meetings refresh completed');
            } catch (error) {
              console.error('Periodic meetings refresh failed:', error);

              // On network errors, increase the refresh interval temporarily
              if (error instanceof Error && error.message.includes('network')) {
                console.log('Network error detected - reducing refresh frequency temporarily');
                // The periodic task will continue with normal interval, but individual failures are handled gracefully
              }
            }
          } else if (currentState.isLoading) {
            console.log('Skipping periodic refresh - already loading data');
          }
        }
      );

      // Mark as setup to prevent duplicate instances
      hasSetupPeriodicRefresh.current = true;

      return () => {
        // Clean up periodic task when component unmounts or user/account changes
        backgroundLoadingService.removePeriodicTask('meetings-periodic-refresh');
        hasSetupPeriodicRefresh.current = false;
      };
    }
  }, [user, account]); // REMOVED: globalLoadingState.completedStages

  const handleMeetingUpdate = async (meetingId: string, fieldsToUpdate: Partial<Record<string, unknown>>) => {
    try {
      const updatedMeetings = meetings.map(meeting =>
        meeting.id === meetingId
          ? { ...meeting, fields: { ...meeting.fields, ...fieldsToUpdate } }
          : meeting
      );
      setMeetings(updatedMeetings);
      await getDataSource().updateMeeting(meetingId, fieldsToUpdate);
      // Invalidate session cache so next refresh fetches fresh data
      if (account) {
        dataLoadingService.invalidateMeetingsCache(account);
      }
    } catch (error) {
      console.error('Error updating meeting in AppContent:', error);
      setError('Failed to update meeting. Please try again.');
    }
  };

  // Determine if we should show the loading screen
  // Only show for authenticated users who are in critical loading state
  const showLoadingScreen = globalLoadingState.isCriticalLoading && user;

  // Debug logging for loading screen visibility
  useEffect(() => {
    // console.log(`Loading screen condition: isCriticalLoading=${globalLoadingState.isCriticalLoading}, user=${!!user}, showLoadingScreen=${showLoadingScreen} - ${new Date().toISOString()}`);
  }, [showLoadingScreen, globalLoadingState.isCriticalLoading, user]);

  // Debug logging for meetings state
  useEffect(() => {
    // console.log(`App meetings state updated: ${meetings.length} meetings, loadingInitial=${loadingInitial} - ${new Date().toISOString()}`);
  }, [meetings.length, loadingInitial]);

  // Memoize the loading screen to prevent re-renders when user object changes
  // Only re-render if user goes from null to object or vice versa
  const loadingScreenComponent = useMemo(() => {
    return user ? <UnifiedLoadingScreen /> : null;
  }, [!!user]); // Only depend on user existence, not the user object itself

  // Render both loading screen and routes, using CSS to control visibility
  // This prevents component unmounting/remounting during loading transitions
  return (
    <>
      {/* Loading screen - always rendered once user exists, visibility controlled by CSS */}
      {/* This prevents mount/unmount cycles during authentication */}
      <div
        style={{
          display: showLoadingScreen ? 'flex' : 'none',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}
        aria-hidden={!showLoadingScreen}
      >
        {/* Render the memoized loading screen component */}
        {/* This ensures unauthenticated users never see the loading screen */}
        {loadingScreenComponent}
      </div>


      {/* Main app content - hidden with CSS during loading */}
      <div style={{
        display: showLoadingScreen ? 'none' : 'block'
      }}>
        <Routes>
      <Route
        path="/login"
        element={
          <LoginPage />
        }
      />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout>
              <MeetingsPage
                meetings={meetings}
                loadingInitial={loadingInitial}
                loadingMoreMeetings={loadingMoreMeetings}
                error={error}
                account={account}
                handleMeetingUpdate={handleMeetingUpdate}
              />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <PrivateRoute>
            <Layout>
              <CalendarPage
                meetings={meetings}
                account={account}
                loading={loadingInitial}
                error={error}
                handleMeetingUpdate={handleMeetingUpdate}
              />
            </Layout>
          </PrivateRoute>
        }
      />
    </Routes>
      </div>
    </>
  );
};

// Error Fallback Component
const ErrorFallback: React.FC<{ error: Error; resetError: () => void }> = ({
  error,
  resetError,
}) => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t('errors.somethingWentWrong')}
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Hemos sido notificados sobre este error y lo solucionaremos pronto.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
        >
          Intentar de nuevo
        </button>
      </div>
    </div>
  );
};

// App component now sets up providers and renders AppContent
function App() {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <LanguageProvider>
        <TimezoneProvider>
          <BrowserRouter>
            <GlobalLoadingProvider>
              <AuthProvider>
                <ToastProvider />
                <AppContent key="stable-app-content" />
              </AuthProvider>
            </GlobalLoadingProvider>
          </BrowserRouter>
        </TimezoneProvider>
      </LanguageProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;